import { useState, useEffect, useMemo } from 'react';

import { TaskFull } from 'interfaces/task.interface';
import { Equipment, FORMAT_DATE_API, RootState, ShortUser, dayjs } from '@umanforge/common';
import { CalendarEvent } from 'interfaces/calendar.interface';
import { DateLocaleService } from 'services/dateLocale.service';
import { GlobalService } from 'services/global.service';
import { TabItem } from '@common_components/buttons/Tabs.component';
import { SectorSettings } from 'interfaces/setting.interface';
import { RoleTaskFullIds } from 'interfaces/roleTask.interface';
import { DragCase, Line, Column, ChronogramService } from 'services/chronogram.service';
import { ToastService } from 'services/toast.service';
import { useTranslation } from 'react-i18next';
import { RoleTaskService } from 'services/roleTask.service';
import { useDispatch, useSelector } from 'react-redux';
import { CALENDAR_CONST, DEFAULT_VALUES } from 'constants/calendar.constants';
import { TaskEquipment } from 'interfaces/task_equipment.interface';
import { Location } from 'interfaces/location.interface';
import { TitleEvent } from '../components/titleEvent.component';
import { TimeGrid } from 'interfaces/timegrid.interface';
import { TaskService } from 'services/task.service';

const toastService: ToastService = new ToastService( '' );

export const useChronogramComponent = (
  tasks: TaskFull[],
  users: ShortUser[],
  roleTasks: RoleTaskFullIds[],
  equipments: Equipment[],
  locations: Location[],
  taskEquipments: TaskEquipment[],
  selectedTab: TabItem,
  isWeek: boolean,
  refreshTask: ( ) => void,
  timegrid: TimeGrid,
  timeGridUsers: Map<string, TimeGrid>,
  currentDate: dayjs.Dayjs = dayjs(),
  settings?: SectorSettings,
  isAdmin: boolean = false,
  openTaskModal?: string
) => {

  const { t } = useTranslation();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const dispatch = useDispatch();

  const [ hoursRange, setHoursRange ] = useState<{ start: number, end: number }>( { start: DEFAULT_VALUES.HOURSTART, end: DEFAULT_VALUES.HOUREND } );
  const [ columnRange, setColumnRange ] = useState<{ start: number, end: number }>( { start: DEFAULT_VALUES.HOURSTART, end: DEFAULT_VALUES.HOUREND } );
  const [ columns, setColumns ] = useState<Column[]>( [] );
  const [ weekend, setWeekEnd ] = useState( false );

  const [ isModalOpen, setIsModalOpen ] = useState<boolean>( false );

  const [ dragCase, setDragCase ] = useState<DragCase>( );
  const [ oldCase, setOldCase ] = useState<DragCase>( );
  const [ dragEvent, setDragEvent ] = useState<CalendarEvent>(  );
  const [ allowedUsers, setAllowedUsers ] = useState<ShortUser[]>( [] );
  const [ draggedTaskRoleTasks, setDraggedTaskRoleTasks ] = useState<RoleTaskFullIds[]>();

  const [ calendarEvents, setCalendarEvents ] = useState<CalendarEvent[]>( [] );
  const [ currentEvent, setCurrentEvent ] = useState<CalendarEvent>(  );
  const [ offset, setOffset ] = useState( Math.floor( 60 / ( DEFAULT_VALUES.STEP ) ) + 2 );

  const [ sortedList, setSortedList ] = useState<Line[]>( [  ] );
  const [ lines, setLines ] = useState<Line[]>( [] );
  const [ eventOverId, setEventOverId ] = useState<string | undefined>( undefined );

  // ------ USE EFFECTS ------

  useEffect( () => {
    setCalendarEvents( tasks.map( task => {
      return new CalendarEvent( task );
    } ) );
  }, [ tasks ] );


  useEffect( () => {
    getLines();
  }, [ calendarEvents, roleTasks, equipments, selectedTab ] );


  useEffect( () => {
    const columnsTmp: Column[] = [];
    const step = 1 / Math.floor( 60 / ( settings?.calendarStep?.value ?? DEFAULT_VALUES.STEP ) );

    if ( isWeek ) {
      const range = ( weekend ? CALENDAR_CONST.WEEKDAYS : CALENDAR_CONST.WEEKDAYSWITHOUTWEEKEND );
      setColumnRange( { start: 1, end: range } );

      const subColumns: string[] = [];

      for ( let i = 0; i < range; i++ ) {
        const day = currentDate.startOf( 'week' ).add( i, 'day' );
        columnsTmp.push( {
          value: i,
          name: DateLocaleService.displayDate( day, 'ddd DD' ),
          rawValue: day.format( FORMAT_DATE_API ),
          subColumns
        } );
      }
    }
    else {
      setColumnRange( { ...hoursRange } );
      GlobalService.hoursByRange( hoursRange, step ).forEach( ( hour, index ) => {
        if ( index % ( ( offset - 2 ) / 2 ) !== 0 ) {
          columnsTmp.push( {
            value: hour,
            name: '',
            rawValue: GlobalService.decimalToHour( hour )
          } );
        }
        else {
          columnsTmp.push( {
            value: hour,
            name: GlobalService.decimalToHourColapsed( hour ),
            rawValue: GlobalService.decimalToHour( hour )
          } );
        }
      } );
    }

    setOffset( isWeek ? 1 : Math.floor( 60 / ( settings?.calendarStep?.value ?? DEFAULT_VALUES.STEP ) ) + 2 );
    setColumns( columnsTmp );
    setColumnRange( { ...hoursRange } );
  }, [ lines, hoursRange ] );


  useEffect( () => {
    let minHour = DEFAULT_VALUES.HOURSTART;
    let maxHour = DEFAULT_VALUES.HOUREND;

    if ( timegrid && timegrid.timeStart < timegrid.timeEnd ) {
      minHour = dayjs( timegrid.timeStart ).hour();
      maxHour = dayjs( timegrid.timeEnd ).hour();
    }
    else if ( timegrid && timegrid.timeStart > timegrid.timeEnd ) {
      minHour = 0;
      maxHour = 24;
    }

    setHoursRange( {
      start: minHour,
      end: maxHour
    } );
    setWeekEnd( ( settings?.showWeekend?.value ?? DEFAULT_VALUES.SHOWWEEKEND ) == 1 );
  }, [ settings, timegrid ] );

  useEffect( () => {
    const openTaskModalReq = async ( taskId: string ) => {
      const _task = await TaskService.getTaskFullById( taskId, token, dispatch );
      if ( _task ) {
        openModal( {
          id: openTaskModal,
          task: _task
        } as CalendarEvent );
      }
    };

    if ( openTaskModal != null ) {
      const _task = tasks.find( ta => { return ta.id == openTaskModal; } );
      if ( !_task ) {
        openTaskModalReq( openTaskModal );
      }
      else {
        openModal( {
          id: openTaskModal,
          task: _task
        } as CalendarEvent );
      }
    }
  }, [ openTaskModal ] );

  // ------ FUNCTIONS ------ //

  const openModal = ( event: CalendarEvent ) => {
    if ( !isModalOpen ) {
      setCurrentEvent( event );
      setIsModalOpen( true );
    }
  };

  const lineAccessor = useMemo( () => {
    const custom = ( dateHour: string, userId: string ) => {
      if ( isWeek ) {
        return '';
      }

      // utilisation de la grille des temps du user
      if ( timegrid.id == 'no_timegrid' ) {
        const userTimeGrid = timeGridUsers.get( userId );
        if ( userTimeGrid ) {

          // time grid en cheuvauchement sur 2 jours
          if ( userTimeGrid.timeStart > userTimeGrid.timeEnd ) {
            const virtualBreaktimeStart = dayjs( userTimeGrid.timeStart ).format( 'HH:mm' );
            const virtualBreaktimeEnd = dayjs( userTimeGrid.timeEnd ).format( 'HH:mm' );

            if ( dateHour < virtualBreaktimeStart && dateHour >= virtualBreaktimeEnd ) {
              return 'bg-[#cecece]';
            }
          }

          // cas normal
          else {
            if ( dateHour < dayjs( userTimeGrid.timeStart ).format( 'HH:mm' ) || dateHour >= dayjs( userTimeGrid.timeEnd ).format( 'HH:mm' ) ) {
              return 'bg-[#cecece]';
            }
          }

          // pauses
          if ( ( userTimeGrid.breaktimes ?? [] ).length > 0 ) {
            if ( ( userTimeGrid.breaktimes ?? [] ).some( breaktime => {
              return !( dateHour < dayjs( breaktime.timeStart ).format( 'HH:mm' ) || dateHour >= dayjs( breaktime.timeEnd ).format( 'HH:mm' ) );
            } ) ) {
              return 'bg-[#cecece]';
            }
          }
        }
        return '';
      }

      // utilisation de la grille es temps selectionné
      else {

        // time grid en cheuvauchement sur 2 jours
        if ( timegrid && timegrid.timeStart > timegrid.timeEnd ) {
          const virtualBreaktimeStart = dayjs( timegrid.timeStart ).format( 'HH:mm' );
          const virtualBreaktimeEnd = dayjs( timegrid.timeEnd ).format( 'HH:mm' );

          if ( dateHour < virtualBreaktimeStart && dateHour >= virtualBreaktimeEnd ) {
            return 'bg-[#cecece]';
          }
        }

        // cas normal
        else {
          if ( dateHour < dayjs( timegrid.timeStart ).format( 'HH:mm' ) || dateHour >= dayjs( timegrid.timeEnd ).format( 'HH:mm' ) ) {
            return 'bg-[#cecece]';
          }
        }

        // pauses
        if ( timegrid.breaktimes.length > 0 ) {
          if ( timegrid.breaktimes.some( breaktime => {
            return !( dateHour < dayjs( breaktime.timeStart ).format( 'HH:mm' ) || dateHour >= dayjs( breaktime.timeEnd ).format( 'HH:mm' ) );
          } ) ) {
            return 'bg-[#cecece]';
          }
        }
      }

      return '';
    };
    return custom;
  }, [ timegrid, timeGridUsers, isWeek ] );



  const getLines = (): void => {
    let tLines: Line[] = [];

    calendarEvents.forEach( calEvent => {
      if ( roleTasks.some( rt => { return rt.idTask == calEvent.task?.id && rt.count > rt.users.length; } ) ) {
        calEvent.understaffed = true;
      }
    } );

    switch ( selectedTab.id ) {

    case 'distribution':
      tLines = getDistributionLines();
      break;

    case 'location':
      tLines = getLocationLines();
      break;

    case 'task':
      tLines = getTaskLines();
      break;

    case 'equipment':
      tLines = getEquipementLines();
      break;

    default: tLines = [];
    }

    if ( isWeek ) {
      tLines.forEach( line => {
        const events = ChronogramService.sortEventsWeek( line.events, currentDate );
        line.events = events.map( event => {
          if ( events.some( ev => {
            if ( ev.task == undefined || event.task == undefined ) {
              return false;
            }
            const startA = dayjs( ev.task.dateStart );
            const endA = dayjs( ev.task.dateEnd );

            const startB = dayjs( event.task.dateStart );
            const endB = dayjs( event.task.dateEnd );
            return ev.id != event.id && startA.isBefore( endB ) && startB.isBefore( endA );
          } ) ) {
            event.stacked = true;
          }
          return event;
        } );
      } );
    }
    else {
      tLines.forEach( line => {
        line.events = [ ...ChronogramService.sortEventsDay( line.events ) ];
      } );
    }

    setLines( [ ...tLines ] );
    setSortedList( [ ...tLines ] );
  };

  const getUserNameOfTask = ( event: CalendarEvent ): string[] => {
    const userIds: string[] = [];
    const res: string[] = [];
    roleTasks.filter( rt => { return rt.idTask == event.task?.id; } ).forEach( rt => {
      userIds.push( ...rt.users );
    } );
    [ ...new Set( userIds ) ].forEach( uId => {
      const user = users.find( u => { return u.id == uId; } );
      if ( user != null ) {
        res.push( user.fullname );
      }
    } );
    return res;
  };

  const getDistributionLines = (): Line[] => {
    const tLines: Line[] = [
      ...users.map( user => {
        return {
          id: user.id,
          name: user.fullname,
          events: [],
        };
      } ),
      {
        id: 'not_assigned',
        name: t( 'translation:errors.not_assigned' ),
        events: [],
      },
    ];

    const roleTasksByTask = new Map<string, RoleTaskFullIds[]>();
    roleTasks.forEach( rt => {
      if ( !roleTasksByTask.has( rt.idTask ) ) {
        roleTasksByTask.set( rt.idTask, [] );
      }
    roleTasksByTask.get( rt.idTask )!.push( rt );
    } );

    const seenTaskIds = new Set<string>();

    tLines.forEach( line => {
      if ( line.id !== 'not_assigned' ) {
      // get role task with the user
        const userRoleTasks = roleTasks.filter( rt => {
          return rt.users.includes( line.id );
        } );
        const taskIds = new Set( userRoleTasks.map( rt => {return rt.idTask;} ) );

        // affect each task to his line
        calendarEvents.forEach( calEvent => {
          const taskId = calEvent.task?.id ?? '';
          if ( taskIds.has( taskId ) && !seenTaskIds.has( taskId ) ) {
            seenTaskIds.add( taskId );
            calEvent.understaffed = false;
            calEvent.title = <TitleEvent
              taskName={ calEvent.task?.taskAction.name }
              location={ calEvent.task?.location.name }
            />;
            line.events.push( { ...calEvent } );
          }
        } );
      } else {
        calendarEvents.forEach( calEvent => {
          const taskId = calEvent.task?.id ?? '';
          const rts = roleTasksByTask.get( taskId );

          if ( !seenTaskIds.has( taskId ) && ( !rts || rts.some( rt => { return rt.users.length < rt.count; } ) ) ) {
            seenTaskIds.add( taskId );
            if ( !rts ) {
              calEvent.understaffed = false;
            }
            else {
              calEvent.understaffed = true;
            }
            calEvent.title = <TitleEvent
              taskName={ calEvent.task?.taskAction.name }
              location={ calEvent.task?.location.name }
            />;
            line.events.push( { ...calEvent } );
          }
        } );
      }
    } );

    return tLines;
  };

  const getTaskLines = (): Line[] => {
    const groupedLines = new Map<string, Line>();

    tasks.forEach( task => {
      const missionName = task.taskAction?.name ?? 'no_mission_name';
      if ( !groupedLines.has( missionName ) ) {
        groupedLines.set( missionName, {
          id: missionName,
          name: missionName,
          events: [],
        } );
      }
    } );

    const tLines = [ ...groupedLines.values() ];

    calendarEvents.forEach( ev => {
      if ( ev.task?.id ) {
        const missionName = ev.task.taskAction?.name ?? 'no_mission_name';
        const line = groupedLines.get( missionName );
        if ( line ) {
          ev.title = <TitleEvent
            location={ ev.task?.location.name }
            users={ getUserNameOfTask( ev ) }
          />;
          line.events.push( { ...ev } );
        }
      }
    } );

    return tLines;
  };

  const getEquipementLines = (): Line[] => {
    const tLines: Line[] = equipments.map( equipment => {
      return {
        id: equipment.id,
        name: equipment.name,
        events: [],
      };
    } );

    const equipmentTaskMap = new Map<string, Set<string>>();
    taskEquipments.forEach( te => {
      if ( !equipmentTaskMap.has( te.idEquipment ) ) {
        equipmentTaskMap.set( te.idEquipment, new Set() );
      }
    equipmentTaskMap.get( te.idEquipment )!.add( te.idTask );
    } );

    tLines.forEach( line => {
      const taskIds = equipmentTaskMap.get( line.id );

      if ( taskIds ) {
        calendarEvents.forEach( calEvent => {
          if ( taskIds.has( calEvent.task?.id ?? '' ) ) {
            calEvent.title = <TitleEvent
              taskName={ calEvent.task?.taskAction.name }
              location={ calEvent.task?.location.name }
              users={ getUserNameOfTask( calEvent ) }
            />;
            line.events.push( { ...calEvent } );
          }
        } );
      }
    } );

    return tLines;
  };

  const getLocationLines = (): Line[] => {
    const tLines: Line[] = locations.map( location => {
      return {
        id: location.id,
        name: location.name,
        events: [],
      };
    } );

    const locationTaskMap = new Map<string, Set<string>>();
    tasks.forEach( task => {
      if ( !locationTaskMap.has( task.location.id ) ) {
        locationTaskMap.set( task.location.id, new Set() );
      }
      locationTaskMap.get( task.location.id )!.add( task.id );
    } );

    tLines.forEach( line => {
      const taskIds = locationTaskMap.get( line.id );

      if ( taskIds ) {
        calendarEvents.forEach( calEvent => {
          if ( taskIds.has( calEvent.task?.id ?? '' ) ) {
            calEvent.title = <TitleEvent
              taskName={ calEvent.task?.taskAction.name }
              users={ getUserNameOfTask( calEvent )}
            />;
            line.events.push( { ...calEvent } );
          }
        } );
      }
    } );

    return tLines;
  };

  const fetchAllowedUsers = async ( allowedRoleTasks: RoleTaskFullIds[] ) => {
    const usersId = users.map( u => { return u.id; } );
    const req = [];
    for ( const roleTask of allowedRoleTasks ) {
      req.push( RoleTaskService.getUsersByAbilitiesSkillsUsers(
        usersId,
        roleTask.abilities,
        roleTask.skills.map( s => { return { id: s.id, value: s.iluo }; } ),
        token,
        dispatch
      ) );
    }
    const res = await Promise.all( req );
    setAllowedUsers( [ ...res.flat() ] );
  };



  const triggerOverEvent = ( id: string | undefined ) => {
    if ( !id ) {
      setCalendarEvents( calendarEvents.map( event => {
        event.allDayOver = false;
        return event;
      } ) );
    }
    else {
      setCalendarEvents( calendarEvents.map( event => {
        event.allDayOver = event.task?.id == id;
        return event;
      } ) );
    }
    setEventOverId( id );
  };

  const onDragStart = ( dCase: DragCase, ev: CalendarEvent ) => {
    if ( isAdmin ) {
      setOldCase( dCase );
      setDragEvent( ev );
      setDraggedTaskRoleTasks( roleTasks.filter( rt => {
        return rt.idTask == ev.task?.id;
      } ) );

      const allowedRoleTasks: RoleTaskFullIds[] = roleTasks.filter( rt => {
        if ( rt.idTask == ev.task?.id && rt.count > rt.users.length ) {
          return true;
        }
        return rt.idTask == ev.task?.id && rt.users.includes( dCase.line.id );
      } );
      if ( allowedRoleTasks && allowedRoleTasks.length > 0 ) {
        fetchAllowedUsers( allowedRoleTasks );
      }
      else {
        setAllowedUsers( [] );
      }
    }
  };

  const cleanupDrag = (): void => {
    // remove tmp event
    sortedList.forEach( line => {
      line.events = line.events.filter( ev => {return !ev.temporary;} );
    } );

    setSortedList( [ ...sortedList ] );

    setDragEvent( undefined );
    setDragCase( undefined );
    setOldCase( undefined );
    setAllowedUsers( [] );
  };

  const onDragEnd = async ( allowed: boolean = false ): Promise<void> => {
    if ( !dragCase || !dragEvent || !dragEvent.task || !isAdmin ) {
      cleanupDrag();
      return;
    }

    const task = dragEvent.task;
    const now = dayjs();

    const startTime = dayjs( dragEvent.start )
      .startOf( 'day' )
      .add( Math.floor( dragCase.hour ), 'hour' )
      .add( Math.round( ( dragCase.hour % 1 ) * 60 ), 'minute' );

    const isForbiddenStatus = [ 'LATE', 'DONE', 'CANCELED' ].includes( task.status ?? '' );
    const isInPast = startTime.isBefore( now );

    if ( isForbiddenStatus || isInPast ) {
      toastService.showWarning( t( 'translation:errors.you_cannot_move_task_in_the_past' ) );
      cleanupDrag();
      return;
    }

    if ( !allowed || !oldCase || !draggedTaskRoleTasks ) {
      toastService.showWarning( t( 'translation:errors.this_users_doesnt_have_authorizations_skills_to_replace_user' ) );
      cleanupDrag();
      return;
    }

    // replace old user by the new one
    if ( !( oldCase.line.id === 'not_assigned' && dragCase.line.id === 'not_assigned' ) && selectedTab.id === 'distribution' ) {
      const updatedRoleTasks: RoleTaskFullIds[] = draggedTaskRoleTasks.map( drt => {
        let _users = drt.users;

        // push
        if ( oldCase.line.id === 'not_assigned' ) {
          if ( _users.length < drt.count ) {
            _users = [ ..._users, dragCase.line.id ];
          }
        }
        // delete
        else if ( dragCase.line.id === 'not_assigned' ) {
          _users = _users.filter( u => {return u !== oldCase.line.id;} );
        }
        // update
        else {
          _users = _users.map( u => {return ( u === oldCase.line.id ? dragCase.line.id : u );} );
        }

        return { ...drt, users: _users };
      } );

      await RoleTaskService.postRoleTaskFullByTask(
        task.id,
        RoleTaskService.fullIdsToFullPost( updatedRoleTasks, task.id ),
        token,
        dispatch,
        () => { toastService.showError( t( 'translation:errors.this_users_doesnt_have_authorizations_skills_to_replace_user' ) ); },
        () => { toastService.showWarning( t( 'translation:errors.this_users_doesnt_have_authorizations_skills_to_replace_user' ) ); }
      );
      refreshTask();
    }

    cleanupDrag();
  };

  const handleDragCaseChange = ( dCase: DragCase | undefined ) => {
    if ( isAdmin ) {
      if ( dCase ) {
        // remove tmp event
        sortedList.map( line => {
          line.events = [ ...line.events.filter( ev => { return !ev.temporary; } ) ];
        } );

        // add tmp event
        sortedList.map( line => {
          if ( line.id == dCase.line.id ) {
            line.events.push( {
              ... dragEvent,
              temporary: true,
              start: dayjs( dragEvent?.start ).toDate(),
              end: dayjs( dragEvent?.end ).toDate()
            } );
          }
        } );
      }
      setSortedList( [ ...sortedList ] );
      setDragCase( dCase );
    }
  };


  const totalHours = ( line: Line ) => {
    const getMinutesOfDay = ( value: string ) => {
      const parsed = dayjs( value );
      return parsed.hour() * 60 + parsed.minute();
    };

    const getGridIntervalsForDate = ( grid: TimeGrid, date: dayjs.Dayjs ) => {
      const gridStart = getMinutesOfDay( grid.timeStart );
      const gridEnd = getMinutesOfDay( grid.timeEnd );

      const dayStart = date.startOf( 'day' );

      if ( gridStart <= gridEnd ) {
        return [ {
          start: dayStart.clone().hour( Math.floor( gridStart / 60 ) ).minute( gridStart % 60 ),
          end: dayStart.clone().hour( Math.floor( gridEnd / 60 ) ).minute( gridEnd % 60 )
        } ];
      }

      return [
        {
          start: dayStart.clone().hour( Math.floor( gridStart / 60 ) ).minute( gridStart % 60 ),
          end: dayStart.clone().endOf( 'day' )
        },
        {
          start: dayStart.clone().startOf( 'day' ),
          end: dayStart.clone().hour( Math.floor( gridEnd / 60 ) ).minute( gridEnd % 60 )
        }
      ];
    };

    const getBreakIntervalsForDate = ( grid: TimeGrid, date: dayjs.Dayjs ) => {
      return ( grid.breaktimes ?? [] ).flatMap( breaktime => {
        const breakStart = getMinutesOfDay( breaktime.timeStart );
        const breakEnd = getMinutesOfDay( breaktime.timeEnd );
        const dayStart = date.startOf( 'day' );

        if ( breakStart <= breakEnd ) {
          return [ {
            start: dayStart.clone().hour( Math.floor( breakStart / 60 ) ).minute( breakStart % 60 ),
            end: dayStart.clone().hour( Math.floor( breakEnd / 60 ) ).minute( breakEnd % 60 )
          } ];
        }

        return [
          {
            start: dayStart.clone().hour( Math.floor( breakStart / 60 ) ).minute( breakStart % 60 ),
            end: dayStart.clone().endOf( 'day' )
          },
          {
            start: dayStart.clone().startOf( 'day' ),
            end: dayStart.clone().hour( Math.floor( breakEnd / 60 ) ).minute( breakEnd % 60 )
          }
        ];
      } );
    };

    const getTaskAssignedUserIds = ( taskId: string ) => {
      return [
        ...new Set( roleTasks
          .filter( rt => { return rt.idTask == taskId; } )
          .flatMap( rt => { return rt.users; } ) )
      ];
    };

    const getMinutesInGridForEvent = ( eventStart: dayjs.Dayjs, eventEnd: dayjs.Dayjs, grid?: TimeGrid ) => {
      if ( !grid ) {
        return Math.max( 0, eventEnd.diff( eventStart, 'minute', true ) );
      }

      let totalMinutes = 0;
      const rangeStart = eventStart.startOf( 'day' );
      const rangeEnd = eventEnd.startOf( 'day' );

      for ( let currentDay = rangeStart.clone(); !currentDay.isAfter( rangeEnd ); currentDay = currentDay.add( 1, 'day' ) ) {
        const intervals = getGridIntervalsForDate( grid, currentDay );
        const breaks = getBreakIntervalsForDate( grid, currentDay );

        intervals.forEach( interval => {
          const overlapStart = dayjs( Math.max( eventStart.valueOf(), interval.start.valueOf() ) );
          const overlapEnd = dayjs( Math.min( eventEnd.valueOf(), interval.end.valueOf() ) );

          if ( overlapEnd.isAfter( overlapStart ) ) {
            let overlapMinutes = overlapEnd.diff( overlapStart, 'minute', true );

            breaks.forEach( breakInterval => {
              const breakStart = dayjs( Math.max( overlapStart.valueOf(), breakInterval.start.valueOf() ) );
              const breakEnd = dayjs( Math.min( overlapEnd.valueOf(), breakInterval.end.valueOf() ) );

              if ( breakEnd.isAfter( breakStart ) ) {
                overlapMinutes -= breakEnd.diff( breakStart, 'minute', true );
              }
            } );

            totalMinutes += Math.max( 0, overlapMinutes );
          }
        } );
      }

      return Math.max( 0, totalMinutes );
    };

    let totalMinutes = 0;
    line.events.forEach( e => {
      const start = e.start;
      const end = e.end;

      if ( start == null || end == null || end.getTime() <= start.getTime() ) {
        return;
      }

      const eventStart = dayjs( start );
      const eventEnd = dayjs( end );
      const eventMinutes = eventEnd.diff( eventStart, 'minute', true );

      if ( selectedTab.id == 'distribution' ) {
        const lineGrid = line.id != 'not_assigned' ? timeGridUsers.get( line.id ) : undefined;
        totalMinutes += getMinutesInGridForEvent( eventStart, eventEnd, lineGrid ?? timegrid );
        return;
      }

      if ( e.fullDay || e.allDay || eventMinutes >= ( 23 * 60 ) ) {
        const taskId = e.task?.id ?? '';
        const assignedUserIds = getTaskAssignedUserIds( taskId );

        if ( assignedUserIds.length > 0 ) {
          totalMinutes += assignedUserIds.reduce( ( sum, userId ) => {
            const userGrid = timeGridUsers.get( userId );
            return sum + getMinutesInGridForEvent( eventStart, eventEnd, userGrid ?? timegrid );
          }, 0 );
        }
        else {
          totalMinutes += getMinutesInGridForEvent( eventStart, eventEnd, timegrid );
        }
        return;
      }

      totalMinutes += Math.max( 0, Math.floor( eventMinutes ) );
    } );

    const hours = Math.floor( totalMinutes / 60 ).toString().padStart( 2, '0' );
    const minutes = ( totalMinutes % 60 ).toString().padStart( 2, '0' );

    return `${hours}:${minutes}`;
  };

  const isOutlined = ( line: Line ) : boolean => {
    if ( dragEvent != null && oldCase?.line != null ) {
      if ( selectedTab.id == 'equipment' || selectedTab.id == 'task' ) {
        return line.id == oldCase.line.id;
      }
      else {
        return line.id == 'not_assigned' || allowedUsers.some( u => { return u.id == line.id; } );
      }
    }
    return false;
  };

  return {
    hoursRange, setHoursRange,
    columnRange, setColumnRange,
    columns, setColumns,
    weekend, setWeekEnd,
    isModalOpen, setIsModalOpen,
    dragCase, setDragCase,
    oldCase, setOldCase,
    dragEvent, setDragEvent,
    allowedUsers, setAllowedUsers,
    draggedTaskRoleTasks, setDraggedTaskRoleTasks,
    calendarEvents, setCalendarEvents,
    currentEvent, setCurrentEvent,
    offset, setOffset,
    sortedList, setSortedList,
    lines, setLines,
    onDragStart,
    onDragEnd,
    handleDragCaseChange,
    totalHours,
    isOutlined,
    openModal,
    triggerOverEvent,
    eventOverId,
    lineAccessor
  };
};
